import React from "react";
import { DeanteData } from "../../../Universal/API/Context-API/AppProvider";
import OstatkiDisplay from "../../OstatkiDisplay/OstatkiDisplay";
import { ColorOption, SizeOption, SpecItem, TechnicalFieldConfig } from "./types";

export const normalizeText = (value: unknown) => {
  const text = String(value ?? "").trim();
  return text.length > 0 ? text : null;
};

const appendUnit = (value: unknown, unit: string) => {
  const text = normalizeText(value);
  if (!text) return null;
  return /[a-zA-Zа-яА-Я%]/.test(text) ? text : `${text} ${unit}`;
};

export const hasStock = (unit: DeanteData) => {
  const ostatki = (unit as any).ostatki;
  const ostatkiPoland = (unit as any).ostatkiPoland;
  if (!ostatki && !ostatkiPoland) return false;

  const toNum = (value: unknown) => parseFloat(String(value ?? 0)) || 0;

  const total =
    toNum(ostatki?.FreeSklad) +
    toNum(ostatki?.ReservSklad) +
    toNum(ostatki?.VPutiFree) +
    toNum(ostatki?.VPutiReserv) +
    toNum(ostatki?.FreeSkladMSK) +
    toNum(ostatki?.ReservSkladMSK) +
    toNum(ostatki?.VPutiFreeMSK) +
    toNum(ostatki?.VPutiReservMSK) +
    toNum(ostatkiPoland?.stock);

  return total > 0;
};

export const buildQuickFacts = (unit: DeanteData): string[] => {
  const facts = [unit.full_size, unit.material, unit.poverh]
    .map((value) => normalizeText(value))
    .filter((value): value is string => Boolean(value));

    // console.log('facts', facts)
  return facts.filter((value, index) => facts.indexOf(value) === index);
};


export const resolveSwatchColor = (value: string) => {
  const normalized = value.toLowerCase();

  if (
    normalized.includes("white") ||
    normalized.includes("bianco") ||
    normalized.includes("blanco") ||
    normalized.includes("bia") ||
    normalized.includes("бел")
  ) {
    return "#F8F8F6";
  }

  if (
    normalized.includes("black") ||
    normalized.includes("nero") ||
    normalized.includes("czarn") ||
    normalized.includes("graphite") ||
    normalized.includes("grafit") ||
    normalized.includes("anthracite") ||
    normalized.includes("черн")
  ) {
    return "#1B1B1D";
  }

  if (
    normalized.includes("gold") ||
    normalized.includes("zlot") ||
    normalized.includes("золот")
  ) {
    return "#B99247";
  }

  if (
    normalized.includes("chrome") ||
    normalized.includes("chrom") ||
    normalized.includes("steel") ||
    normalized.includes("stal") ||
    normalized.includes("inox")
  ) {
    return "#B7BCC4";
  }

  if (
    normalized.includes("gray") ||
    normalized.includes("grey") ||
    normalized.includes("szary") ||
    normalized.includes("сер")
  ) {
    return "#8B9098";
  }

  if (normalized.includes("beige") || normalized.includes("беж")) {
    return "#C8B79F";
  }

  return "#D8D5CF";
};

export const parseColorValues = (value: unknown) =>
  String(value ?? "")
    .split(/[,;|/]+/)
    .map((item) => item.trim())
    .filter(Boolean);

const buildFallbackColorOptions = (unit: DeanteData): ColorOption[] => {
  const palValues = parseColorValues(unit.pal);
  const fallbackLabels = [unit.color1, unit.color2, unit.color3]
    .map((value) => normalizeText(value))
    .filter(Boolean) as string[];

  const sourceValues = palValues.length > 0 ? palValues : fallbackLabels;

  return sourceValues
    .filter((value, index, array) => array.indexOf(value) === index)
    .map((value, index) => ({
      key: `color_${index}_${value}`,
      label: fallbackLabels[index] ?? value,
      swatch: resolveSwatchColor(value),
    }));
};

const buildVariantColorLabel = (unit: DeanteData) => {
  const directLabel =
    normalizeText(unit.color1) ||
    normalizeText(unit.color2) ||
    normalizeText(unit.color3);

  if (directLabel) return directLabel;

  const palValue = parseColorValues(unit.pal)[0];
  return normalizeText(palValue) || normalizeText(unit.art) || String(unit.id);
};

const buildVariantSizeLabel = (unit: DeanteData) => {
  const fullSize = normalizeText(unit.full_size);
  const rawParts = (fullSize ? fullSize : [unit.width, unit.height, unit.thick].filter(Boolean).join("x"))
    .split("x")
    .map((part) => String(part).trim())
    .filter(Boolean);

  if (rawParts.length >= 2) {
    const leftRaw = parseFloat(rawParts[0].replace(",", "."));
    const rightRaw = parseFloat(rawParts[rawParts.length - 1].replace(",", "."));

    const left = Number.isFinite(leftRaw) ? Math.round(leftRaw / 10) : rawParts[0];
    const right = Number.isFinite(rightRaw) ? Math.round(rightRaw) : rawParts[rawParts.length - 1];

    return `${left}x${right}`;
  }

  return fullSize || normalizeText(unit.art) || String(unit.id);
};

const buildVariantFamily = (
  unit: DeanteData,
  allUnitData?: Record<string | number, DeanteData> | null
) => {
  const relatedIds = [unit.id, ...(Array.isArray(unit.krat) ? unit.krat : [])]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);

  const uniqueRelatedIds = relatedIds.filter(
    (value, index, array) => array.indexOf(value) === index
  );

  if (!allUnitData) return [];

  return uniqueRelatedIds
    .map((variantId) => allUnitData[variantId] ?? allUnitData[Number(variantId)])
    .filter(Boolean)
    .filter(
      (variantUnit, index, array) =>
        array.findIndex((candidate) => String(candidate.id) === String(variantUnit.id)) === index
    );
};

const TECHNICAL_FIELDS_CONFIG: TechnicalFieldConfig[] = [
  {
    key: "col",
    label: "Коллекция",
    getValue: (unit) => unit.col,
    action: "collection",
    actionValue: (unit) => unit.col,
  },
  {
    key: "art",
    label: "Код продукта",
    getValue: (unit) => unit.art,
    action: "copy",
    actionValue: (unit) => unit.art,
  },
  {
    key: "color1",
    label: "Отделка",
    getValue: (unit) => unit.color1,
  },
  {
    key: "poverh",
    label: "Поверхность",
    getValue: (unit) => unit.poverh,
  },
  {
    key: "material",
    label: "Материал",
    getValue: (unit) => unit.material,
  },
  {
    key: "full_size",
    label: "Размер",
    getValue: (unit) => unit.full_size,
    enabled: false,
  },
  {
    key: "width",
    label: "Ширина",
    getValue: (unit) => appendUnit(unit.width, "мм"),
    enabled: false,
  },
  {
    key: "height",
    label: "Высота",
    getValue: (unit) => appendUnit(unit.height, "мм"),
    enabled: false,
  },
  {
    key: "thick",
    label: "Толщина",
    getValue: (unit) => appendUnit(unit.thick, "мм"),
    enabled: false,
  },
  {
    key: "ves",
    label: "Вес",
    getValue: (unit) => appendUnit(unit.ves, normalizeText(unit.vesED) ?? "кг"),
    enabled: false,
  },
];

export const buildColorOptions = (
  unit: DeanteData,
  allUnitData?: Record<string | number, DeanteData> | null
): ColorOption[] => {
  const variantFamily = buildVariantFamily(unit, allUnitData);
  const currentColorLabel = buildVariantColorLabel(unit);
  const currentSizeLabel = buildVariantSizeLabel(unit);

  if (variantFamily.length > 1) {
    const colorBuckets = new Map<string, DeanteData[]>();

    variantFamily.forEach((variantUnit) => {
      const colorLabel = buildVariantColorLabel(variantUnit);
      const bucket = colorBuckets.get(colorLabel) ?? [];
      bucket.push(variantUnit);
      colorBuckets.set(colorLabel, bucket);
    });

    const uniqueVariantOptions = Array.from(colorBuckets.entries()).map(([colorLabel, variants]) => {
      const matchedByCurrentSize =
        variants.find((variantUnit) => buildVariantSizeLabel(variantUnit) === currentSizeLabel) ??
        variants[0];

      return {
        key: `variant_color_${colorLabel}`,
        label: colorLabel,
        swatch: resolveSwatchColor(colorLabel),
        unitId: matchedByCurrentSize.id,
        isActive: colorLabel === currentColorLabel,
      } satisfies ColorOption;
    });

    if (uniqueVariantOptions.length > 0) {
      return uniqueVariantOptions;
    }
  }

  return buildFallbackColorOptions(unit);
};

export const buildSizeOptions = (
  unit: DeanteData,
  allUnitData?: Record<string | number, DeanteData> | null
): SizeOption[] => {
  const variantFamily = buildVariantFamily(unit, allUnitData);
  if (variantFamily.length <= 1) return [];

  const currentColorLabel = buildVariantColorLabel(unit);
  const currentSizeLabel = buildVariantSizeLabel(unit);
  const sizeBuckets = new Map<string, DeanteData[]>();

  variantFamily.forEach((variantUnit) => {
    const sizeLabel = buildVariantSizeLabel(variantUnit);
    const bucket = sizeBuckets.get(sizeLabel) ?? [];
    bucket.push(variantUnit);
    sizeBuckets.set(sizeLabel, bucket);
  });

  const sizeOptions = Array.from(sizeBuckets.entries()).map(([sizeLabel, variants]) => {
    const matchedByCurrentColor =
      variants.find((variantUnit) => buildVariantColorLabel(variantUnit) === currentColorLabel) ??
      variants[0];

    return {
      key: `variant_size_${sizeLabel}`,
      label: sizeLabel,
      unitId: matchedByCurrentColor.id,
      isActive: sizeLabel === currentSizeLabel,
    } satisfies SizeOption;
  });

  return sizeOptions.length > 1 ? sizeOptions : [];
};

export const buildTechnicalSpecs = ({
  unit,
  isAvailable,
  scaleAll,
}: {
  unit: DeanteData;
  isAvailable: boolean;
  scaleAll: number;
}): SpecItem[] => {
  const items: SpecItem[] = [];

  const pushText = ({
    key,
    label,
    value,
    action,
    actionValue,
  }: {
    key: string;
    label: string;
    value: unknown;
    action?: SpecItem["action"];
    actionValue?: string;
  }) => {
    const text = normalizeText(value);
    if (!text) return;
    items.push({
      key,
      label,
      value: text,
      action,
      actionValue,
    });
  };

  TECHNICAL_FIELDS_CONFIG.forEach((field) => {
    if (field.enabled === false) return;

    pushText({
      key: field.key,
      label: field.label,
      value: field.getValue(unit),
      action: field.action,
      actionValue: field.actionValue?.(unit),
    });
  });

  if (isAvailable) {
    items.push({
      key: "availability",
      label: "Наличие",
      value: <OstatkiDisplay unit={unit} scaleAll={scaleAll} compact={true} />,
      renderAsNode: true,
    });
  }

  return items;
};
